/**
 * Admin Issue Token page (J-A12).
 *
 * System Admin can manually issue a share token bound to any User or Company.
 * Reuses form layout pattern from company share-tokens. POST /api/admin/share-tokens.
 *
 * Route: /admin/users/:id/issue-token
 * The :id param is the user ID for whom the token is being issued.
 */

import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Input } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface IssuedToken {
  id: string;
  token: string;
  test_id: string;
  company_id: string | null;
  issued_to_email: string | null;
  direct_url: string;
  embed_url: string | null;
  embed_snippet: string | null;
}

export function AdminIssueTokenPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const userId = params.id ?? '';

  // Form state
  const [testId, setTestId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [issuedToEmail, setIssuedToEmail] = useState('');
  const [maxUses, setMaxUses] = useState<number>(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [label, setLabel] = useState('');
  const [isEmbed, setIsEmbed] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState('');

  // Submission state
  const [isIssuing, setIsIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<IssuedToken | null>(null);

  // Copy feedback
  const [copied, setCopied] = useState(false);

  const handleIssue = async (): Promise<void> => {
    if (!testId.trim()) {
      setIssueError('Test ID is required');
      return;
    }
    setIsIssuing(true);
    setIssueError(null);
    setIssuedToken(null);

    try {
      const body: Record<string, unknown> = {
        test_id: testId.trim(),
        max_uses: maxUses,
        is_embed_anonymous: isEmbed,
        label: label || undefined,
        issued_to_email: issuedToEmail || undefined,
      };
      if (companyId.trim()) body.company_id = companyId.trim();
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      if (isEmbed && allowedDomains.trim()) {
        body.allowed_embed_domains = allowedDomains
          .split(/[\n,;]+/)
          .map((d) => d.trim())
          .filter((d) => d.length > 0);
      }

      const response = await apiFetch('/api/admin/share-tokens', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });

      const data = (await response.json()) as {
        ok: boolean;
        data?: IssuedToken;
        error?: { message: string };
      };

      if (!data.ok) {
        setIssueError(data.error?.message ?? 'Failed to issue token');
        return;
      }

      if (data.data) {
        setIssuedToken(data.data);
      }
    } catch {
      setIssueError('Network error');
    } finally {
      setIsIssuing(false);
    }
  };

  const handleCopy = (text: string): void => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AdminLayout eyebrow="Admin · Issue Token" title={`Issue Token for User ${userId.slice(0, 8)}…`}>
      <div className="mx-auto max-w-2xl space-y-6">
        <p className="font-sans text-body text-muted">
          Manually issue a Share Token for this user. The token can be bound to a
          specific Company and Test.
        </p>

        <Card variant="default" className="p-6 space-y-4">
          <div>
            <label htmlFor="test-id" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
              Test ID *
            </label>
            <Input
              id="test-id"
              name="test-id"
              type="text"
              placeholder="Enter test ID"
              value={testId}
              onChange={(e) => setTestId(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="company-id" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
              Company ID (optional)
            </label>
            <Input
              id="company-id"
              type="text"
              placeholder="Leave blank for individual token"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="issued-to-email" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
              Candidate email (optional)
            </label>
            <Input
              id="issued-to-email"
              type="email"
              placeholder="candidate@example.com"
              value={issuedToEmail}
              onChange={(e) => setIssuedToEmail(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="label-input" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
              Label (optional)
            </label>
            <Input
              id="label-input"
              type="text"
              placeholder="e.g. Pilot access for Candidate A"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="expires-at" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                Expiration
              </label>
              <Input
                id="expires-at"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="max-uses" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                Usage limit
              </label>
              <Input
                id="max-uses"
                type="number"
                min={0}
                value={maxUses}
                onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
              />
            </div>
          </div>

          {/* Embed toggle */}
          <div className="flex items-center gap-3">
            <input
              id="embed-toggle"
              type="checkbox"
              checked={isEmbed}
              onChange={(e) => setIsEmbed(e.target.checked)}
              className="h-4 w-4 rounded border-line-2 text-accent focus:ring-accent"
            />
            <label htmlFor="embed-toggle" className="font-sans text-body text-ink">
              Embed mode (iframe)
            </label>
          </div>

          {isEmbed && (
            <div>
              <label htmlFor="allowed-domains" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                Allowed domains (one per line)
              </label>
              <textarea
                id="allowed-domains"
                name="allowed-domains"
                placeholder={'example.com\nsub.example.com'}
                value={allowedDomains}
                onChange={(e) => setAllowedDomains(e.target.value)}
                rows={3}
                className="w-full rounded border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
              />
            </div>
          )}

          {issueError && (
            <p className="font-sans text-small text-error" role="alert">{issueError}</p>
          )}

          <Button
            variant="primary"
            type="button"
            onClick={() => void handleIssue()}
            disabled={isIssuing}
            className="w-full"
          >
            {isIssuing ? 'Issuing…' : 'Issue Token'}
          </Button>
        </Card>

        {/* Issued token display */}
        {issuedToken && (
          <Card variant="default" className="p-4 space-y-3 border-accent/30 bg-accent-l/20">
            <h3 className="font-sans text-title font-medium text-accent">
              Token issued successfully
            </h3>
            <div>
              <p className="font-sans text-small text-muted mb-1">Share URL</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded border border-line bg-card px-3 py-2 font-mono text-xs text-ink break-all">
                  {issuedToken.direct_url}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => handleCopy(issuedToken.direct_url)}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
            {issuedToken.embed_snippet && (
              <div>
                <p className="font-sans text-small text-muted mb-1">Embed snippet</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded border border-line bg-card px-3 py-2 font-mono text-xs text-ink break-all">
                    {issuedToken.embed_snippet}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => handleCopy(issuedToken.embed_snippet ?? '')}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
